import { formatarCnpj } from '@favo/core';
import { Link, useSearchParams } from 'react-router-dom';

import { Celula } from '@/componentes/celula';
import { Carregando, Falha, Vazio } from '@/componentes/estado-da-tela';
import { Button } from '@/componentes/ui/button';
import { Input } from '@/componentes/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/componentes/ui/table';
import { Valor } from '@/componentes/valor';
import { mensagemDoErro, useEmpresasAdmin } from '@/dados';

/* A base de cadastros, em tabela.
 *
 * As colunas respondem, nesta ordem: quem é, como falo com essa pessoa, ela
 * tem conta, usou quanto, e quando apareceu pela última vez. Deliberadamente
 * NÃO há coluna de margem nem de preço: o painel lê cadastro e atividade; o
 * número de cada empresa é dela. */

const POR_PAGINA = 50;

export function Empresas() {
  // Busca e página no ENDEREÇO, não no componente: cada nome abre uma tela
  // própria, e voltar dela precisa devolver a lista como estava. `replace`
  // porque digitar na busca não é um passo de navegação — sem ele, o voltar
  // do navegador desfaria a busca letra por letra.
  const [parametros, definirParametros] = useSearchParams();
  const busca = parametros.get('busca') ?? '';
  const pagina = Number(parametros.get('pagina') ?? 1) || 1;
  const lista = useEmpresasAdmin(busca.trim(), pagina);

  const navegar = (mudanca: { busca?: string; pagina?: number }) => {
    const proximo = new URLSearchParams(parametros);
    proximo.set('aba', 'empresas');
    const termo = mudanca.busca ?? busca;
    if (termo) proximo.set('busca', termo);
    else proximo.delete('busca');
    // Digitar na busca volta para a primeira página: filtrar mantendo a página
    // 3 mostra vazio com resultado existindo.
    const destino = mudanca.pagina ?? (mudanca.busca === undefined ? pagina : 1);
    if (destino > 1) proximo.set('pagina', String(destino));
    else proximo.delete('pagina');
    definirParametros(proximo, { replace: true });
  };

  const buscar = (texto: string) => navegar({ busca: texto });

  const dados = lista.data;
  const paginas = dados ? Math.max(1, Math.ceil(dados.total / POR_PAGINA)) : 1;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-tinta-2 text-apoio" aria-live="polite">
          {lista.isPending ? 'Carregando' : `${dados?.total ?? 0} empresas`}
        </p>
        <div className="w-full max-w-campo-longo">
          <label className="sr-only" htmlFor="busca-empresa">
            Buscar por nome, CNPJ ou e-mail
          </label>
          <Input
            id="busca-empresa"
            type="search"
            value={busca}
            onChange={(evento) => buscar(evento.target.value)}
            placeholder="Buscar por nome, CNPJ ou e-mail"
          />
        </div>
      </div>

      {lista.isError ? (
        <Falha
          oQue="a lista de empresas"
          motivo={mensagemDoErro(lista.error)}
          aoTentarDeNovo={() => void lista.refetch()}
        />
      ) : !dados ? (
        <Carregando linhas={2} rotulo="Carregando as empresas." />
      ) : dados.itens.length === 0 ? (
        <Vazio
          titulo={busca ? 'Nenhuma empresa com esse termo.' : 'Nenhuma empresa cadastrada.'}
          ondePreencher={
            busca
              ? 'Ajuste o termo ou limpe o campo para ver todas.'
              : 'As empresas aparecem aqui quando alguém preenche a etapa Empresa.'
          }
          {...(busca
            ? {
                acao: (
                  <Button variant="outline" size="sm" onClick={() => buscar('')}>
                    Limpar busca
                  </Button>
                ),
              }
            : {})}
        />
      ) : (
        <Celula respiro="justo">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Empresa</TableHead>
                  <TableHead scope="col">CNPJ</TableHead>
                  <TableHead scope="col">E-mail do cadastro</TableHead>
                  <TableHead scope="col">Conta</TableHead>
                  <TableHead scope="col" className="text-right">Projetos</TableHead>
                  <TableHead scope="col" className="text-right">Visitas</TableHead>
                  <TableHead scope="col">Último acesso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dados.itens.map((empresa) => (
                  <TableRow key={empresa.id}>
                    <TableCell>
                      {/* O endereço da lista viaja junto para que o voltar da
                          empresa devolva a mesma busca e a mesma página. */}
                      <Link
                        to={`/admin/empresas/${empresa.id}?${parametros.toString()}`}
                        className="font-medium underline-offset-4 hover:underline"
                      >
                        {empresa.nome_empresa}
                      </Link>
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {empresa.cnpj ? formatarCnpj(empresa.cnpj) : '—'}
                    </TableCell>
                    <TableCell className="max-w-64 truncate">{empresa.email}</TableCell>
                    <TableCell className="text-nota">
                      {empresa.anonimo ? (
                        <span className="text-tinta-2">Sem cadastro</span>
                      ) : (
                        <span className="truncate">
                          {empresa.usuario_email}
                          {empresa.papel === 'admin' ? ' · admin' : ''}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Valor valor={empresa.projetos} formato="inteiro" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Valor valor={empresa.acessos} formato="inteiro" />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Valor valor={empresa.ultimo_acesso} formato="data" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Celula>
      )}

      {paginas > 1 ? (
        <div className="flex items-center justify-end gap-3">
          <p className="text-tinta-2 text-nota">
            Página {pagina} de {paginas}
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={pagina <= 1}
            onClick={() => navegar({ pagina: pagina - 1 })}
          >
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pagina >= paginas}
            onClick={() => navegar({ pagina: pagina + 1 })}
          >
            Próxima
          </Button>
        </div>
      ) : null}
    </div>
  );
}
